import React, { useState, useEffect, useRef } from 'react';
import { Search, Building2, User, Heart, X, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../lib/api';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

interface LookupResult {
  id: string;
  name: string;
  type: 'lobbyist' | 'legislator' | 'nonprofit';
  source_id: string;
  match_confidence: number;
  subtitle: string;
  status: string;
  data: {
    primary_name: string;
    suggested_org_category: string;
    [key: string]: any;
  };
}

interface OrganizationLookupFieldProps {
  onSelect: (result: LookupResult) => void;
  onClear: () => void;
  placeholder?: string;
  sources?: string[];
  selectedResult?: LookupResult | null;
  className?: string;
}

const OrganizationLookupField: React.FC<OrganizationLookupFieldProps> = ({
  onSelect,
  onClear,
  placeholder = "Search existing organizations, people, or entities...",
  sources = ['lobbyist', 'legislator', 'nonprofit'],
  selectedResult = null,
  className = ""
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<LookupResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search function
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      await performSearch(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, sources]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (query: string) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setError(null);

    try {
      const response = await api.get('/admin/organization-lookup/search', {
        params: {
          q: query,
          sources: sources.join(','),
          limit: 10
        }
      });

      if (response.data.success) {
        setResults(response.data.data.results);
        setShowResults(true);
      } else {
        setError('Search failed');
        setResults([]);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.response?.data?.error || 'Search failed');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelect = (result: LookupResult) => {
    onSelect(result);
    setSearchQuery('');
    setShowResults(false);
    setResults([]);
  };

  const handleClear = () => {
    onClear();
    setSearchQuery('');
    setShowResults(false);
    setResults([]);
    setError(null);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lobbyist':
        return <Building2 className="w-4 h-4 text-blue-600" />;
      case 'legislator':
        return <User className="w-4 h-4 text-green-600" />;
      case 'nonprofit':
        return <Heart className="w-4 h-4 text-purple-600" />;
      default:
        return <Building2 className="w-4 h-4 text-neutral-600" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lobbyist':
        return 'Lobbyist';
      case 'legislator':
        return 'Legislator';
      case 'nonprofit':
        return 'Non-profit';
      default:
        return 'Organization';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return 'text-green-600';
    if (confidence >= 80) return 'text-yellow-600';
    return 'text-neutral-600';
  };

  if (selectedResult) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full p-4 border-2 border-green-200 bg-green-50 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                {getTypeIcon(selectedResult.type)}
                <span className="text-sm font-medium text-green-800">
                  {getTypeLabel(selectedResult.type)} Selected
                </span>
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1">
                {selectedResult.name}
              </h4>
              <p className="text-sm text-neutral-600 mb-2">
                {selectedResult.subtitle}
              </p>
              <div className="text-xs text-green-700">
                Organization form will be populated with available data from this entity.
              </div>
            </div>
            <button
              onClick={handleClear}
              className="ml-3 p-1 text-neutral-500 hover:text-neutral-600 rounded-full hover:bg-white"
              title="Clear selection and search manually"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <form onSubmit={(e) => e.preventDefault()} className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-neutral-500" />
        </div>
        <input
          type="text"
          id="organization-lookup-search"
          name="organization-lookup-search"
          autoComplete="off"
          value={searchQuery}
          onChange={(e) => {
            e.stopPropagation();
            setSearchQuery(e.target.value);
          }}
          onFocus={() => {
            if (results.length > 0) setShowResults(true);
          }}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-3 border border-border-light-secondary rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        />
        {isSearching && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </form>

      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {showResults && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border-light rounded-lg shadow-lg max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-border-light">
            <div className="text-sm text-neutral-600">
              Found {results.length} matching entities
            </div>
          </div>
          {results.map((result) => (
            <button
              key={result.id}
              onClick={() => handleSelect(result)}
              className="w-full p-4 text-left hover:bg-background-tertiary border-b border-border-light last:border-b-0 focus:outline-none focus:bg-background-tertiary"
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getTypeIcon(result.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-neutral-900 truncate">
                      {result.name}
                    </h4>
                    <div className="flex items-center space-x-2 ml-2">
                      <span className="text-xs px-2 py-1 bg-background-tertiary text-neutral-700 rounded-full">
                        {getTypeLabel(result.type)}
                      </span>
                      <span className={`text-xs ${getConfidenceColor(result.match_confidence)}`}>
                        {result.match_confidence}% match
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 truncate">
                    {result.subtitle}
                  </p>
                  <div className="flex items-center mt-2 space-x-4">
                    <span className="text-xs text-neutral-500">
                      Status: {result.status}
                    </span>
                    <span className="text-xs text-blue-600">
                      → Will suggest: {result.data.suggested_org_category}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && searchQuery.trim().length >= 2 && !isSearching && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border-light rounded-lg shadow-lg p-4">
          <div className="text-center text-neutral-500">
            <Search className="w-8 h-8 mx-auto mb-2 text-neutral-500" />
            <p className="text-sm">No existing entities found for &quot;{searchQuery}&quot;</p>
            <p className="text-xs text-neutral-500 mt-1">
              You can continue with manual entry below
            </p>
          </div>
        </div>
      )}

      {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
        <div className="mt-2 text-xs text-neutral-500">
          Enter at least 2 characters to search
        </div>
      )}
    </div>
  );
};

export default OrganizationLookupField;
